type QueueHandler<
  T extends {
    id?: string;
  },
> = (items: T[]) => void | Promise<void>;

export class DebouncedQueue<
  T extends {
    id?: string;
  },
> {
  private queue: T[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private handler: QueueHandler<T>,
    private delay: number
  ) {}

  /**
   * Adds an item to the queue and resets the debounce timer.
   * @param item The item to add to the queue.
   */
  public add(items: T[]): void {
    items.map((item) => {
      if (!item.id) {
        this.queue.push(item);
      } else {
        const existingQueueItem = this.queue.findIndex(
          (existingItem) => existingItem?.id === item.id
        );

        if (existingQueueItem !== -1) {
          this.queue[existingQueueItem] = item;
        } else {
          this.queue.push(item);
        }
      }
    });

    this.resetTimer();
  }

  /**
   * Resets the debounce timer.
   */
  private resetTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      this.flush();
    }, this.delay);
  }

  /**
   * Processes all items in the queue immediately and clears the queue.
   */
  public flush(): void {
    if (this.queue.length > 0) {
      this.handler(this.queue);
      this.queue = [];
      this.cancel(); // Clear the timer after flushing
    }
  }

  /**
   * Cancels the pending flush operation and clears the queue.
   */
  public cancel(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.queue = [];
  }
}
